import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiConfig from '../config/apiConfig';
import { useAppState } from '../context/AppStateContext';
import './CityJail.css';

const CityJail = ({ cityId, cityName, onBackClick }) => {
  const [jailedPlayers, setJailedPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attemptingJailbreak, setAttemptingJailbreak] = useState(false);
  const [selectedPrisoner, setSelectedPrisoner] = useState(null);
  const [jailbreakResult, setJailbreakResult] = useState(null);
  const { inJail, jailReleaseTime } = useAppState();

  useEffect(() => {
    const fetchJailedPlayers = async () => {
      try {
        setLoading(true);
        const response = await fetch(apiConfig.checkCity, {
          method: 'GET',
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch jailed players');
        }

        const data = await response.json();
        console.log('Jailed players:', data);
        setJailedPlayers(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching jailed players:', err);
        setError('Failed to load jailed players. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchJailedPlayers();
  }, []);

  // For other players to attempt to break someone else out
  const attemptToBreakOutPrisoner = async (prisoner) => {
    setSelectedPrisoner(prisoner);
    setAttemptingJailbreak(true);
    
    try {
      const response = await fetch(apiConfig.jailbreak, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ player: prisoner.id })
      });
      
      const data = await response.json();
      
      setJailbreakResult({
        success: data.success,
        message: data.message,
        jailTime: data.jailTime,
        prisoner: prisoner
      });
    } catch (err) {
      console.error('Error attempting jailbreak:', err);
      setJailbreakResult({
        success: false,
        message: 'There was an error attempting the jailbreak. Please try again later.',
        prisoner: prisoner
      });
    } finally {
      setAttemptingJailbreak(false);
    }
  };

  const clearJailbreakResult = () => {
    setJailbreakResult(null);
    setSelectedPrisoner(null);
  };

  // Format the remaining jail time
  const formatRemainingTime = () => {
    if (!jailReleaseTime) return null;
    
    const now = new Date();
    const releaseTime = new Date(jailReleaseTime);
    
    if (now >= releaseTime) return "Release pending...";
    
    const diffMs = releaseTime - now;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const diffSecs = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    if (diffHrs > 0) {
      return `${diffHrs}h ${diffMins}m ${diffSecs}s remaining`;
    } else {
      return `${diffMins}m ${diffSecs}s remaining`;
    }
  };

  return (
    <div className="city-jail">
      <div className="jail-header">
        <h2>{cityName} Jail</h2>
        <button onClick={onBackClick} className="back-button">
          <span className="back-icon">←</span> Back to City
        </button>
      </div>
      
      {inJail && (
        <div className="jail-status-message">
          <span className="jail-icon">🔒</span>
          <div>
            <p>You are currently imprisoned. You can only visit the jail until your sentence is complete.</p>
            {jailReleaseTime && (
              <p className="jail-timer">
                <strong>Release time:</strong> {new Date(jailReleaseTime).toLocaleString()} 
                <br />
                <span className="countdown">{formatRemainingTime()}</span>
              </p>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {/* Jailbreak attempt result modal */}
      {jailbreakResult && (
        <div className="jailbreak-result">
          <div className="jailbreak-result-content">
            <h3>{jailbreakResult.success ? 'Jail Break Successful!' : 'Jail Break Failed'}</h3>
            <p>{jailbreakResult.message}</p>
            {jailbreakResult.jailTime && !jailbreakResult.success && (
              <p>You've been caught and will be released from jail at: {new Date(jailbreakResult.jailTime).toLocaleString()}</p>
            )}
            <button onClick={clearJailbreakResult} className="close-button">Close</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading jail records...</div>
      ) : (
        <div className="jail-content">
          <h3>Current Prisoners</h3>
          
          {jailedPlayers.length === 0 ? (
            <p className="empty-jail">There are currently no prisoners in this jail.</p>
          ) : (
            <div className="prisoners-list">
              {jailedPlayers.map(player => (
                <div key={player.id} className="prisoner-card">
                  <Link to={`/character/view/${player.id}`} className="prisoner-name">
                    {player.username}
                  </Link>
                  
                  {/* Option for other players to break someone out - only available if you're not in jail */}
                  {!inJail && (
                    <button 
                      className="breakout-button"
                      onClick={() => attemptToBreakOutPrisoner(player)}
                      disabled={attemptingJailbreak}
                    >
                      Attempt Breakout
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Information for visitors */}
          {!inJail ? (
            <div className="visitor-info">
              <h3>Visitor Information</h3>
              <p>You can attempt to break out prisoners, but doing so carries risks. If caught, you may end up joining them behind bars.</p>
            </div>
          ) : (
            <div className="visitor-info">
              <h3>Prisoner Information</h3>
              <p>As a prisoner, you cannot attempt to break out others. You must wait for your sentence to complete or for someone else to break you out.</p>
            </div>
          )}
        </div>
      )}
      
      {/* Loading overlay for jail break attempts */}
      {attemptingJailbreak && (
        <div className="jailbreak-overlay">
          <div className="jailbreak-attempt">
            <div className="jailbreak-spinner"></div>
            <p>Attempting to break {selectedPrisoner?.username} out of jail...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CityJail;
